// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React from "react";

import { Box, Grid, Paper, styled, Typography } from "@3778.care/goban";
import PropTypes from "prop-types";

import { useMixpanel } from "../../contexts/mixpanel";
import { categories } from "../../domain/insurancePlan/categories";
import { useIntercomService } from "../../routes/services/useIntercomService";
import { hyphenConverter } from "../../util/hyphenConverter";
import { useIcons } from "./components/icons";

const ContainerGrid = styled(Grid)(({ theme: { spacing } }) => ({
  width: `calc(100% + ${spacing(1.5)})`,
  margin: -spacing(0.75),
}));

const ItemGrid = styled(Grid)(({ theme: { spacing } }) => ({
  padding: spacing(0.75),
}));

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isCovid" && prop !== "isHome",
})(({ isCovid, isHome, theme: { palette, spacing } }) => ({
  cursor: "pointer",
  height: "100%",
  width: "100%",
  padding: isHome ? spacing(1) : spacing(1.5, 3, 1, 2.5),
  ...(isCovid && {
    backgroundColor: palette.grey[300],
  }),
}));

export const ServiceCardIntercom = ({
  insurancePlan: {
    category,
    serviceIdentifier,
    isCategory,
    service,
    healthcareService,
    comment: customComment,
  },
  parent,
}) => {
  const { track } = useMixpanel();
  const openIntercom = useIntercomService({ category, isCategory });

  const handleClick = () => {
    if (isCategory) {
      track(`Open category - ${category}`);
    } else {
      track(`Open service - ${service}`);
    }
    openIntercom();
  };

  const isCovid = service === "covid";

  const { name, comment } = isCategory
    ? categories[category]
    : healthcareService;

  const icon = useIcons(category);

  return (
    <StyledPaper
      data-cy="service-card"
      className={isCovid && "ServiceCardCovid"}
      elevation={0}
      id={isCategory ? undefined : serviceIdentifier}
      isCovid={isCovid}
      isHome={parent === "home"}
      onClick={handleClick}
      style={{ boxShadow: "none" }}
      variant={isCovid ? "elevation" : "outlined"}
    >
      <ContainerGrid alignItems="center" container spacing={0}>
        <ItemGrid item xs={!isCovid && 12}>
          <Box display="flex">{icon}</Box>
        </ItemGrid>
        <ItemGrid item>
          <Typography color="textPrimary" component="h4" variant="subtitle2">
            {hyphenConverter(name)}
          </Typography>
          {parent === "services" && (
            <Typography color="textPrimary" component="h4" variant="caption">
              {!isCategory && customComment ? customComment : comment}
            </Typography>
          )}
        </ItemGrid>
      </ContainerGrid>
    </StyledPaper>
  );
};

ServiceCardIntercom.propTypes = {
  insurancePlan: PropTypes.object.isRequired,
  parent: PropTypes.oneOf(["home", "services"]),
};
